import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../contexts/AuthContext';
import { photographerService } from '../../services/photographerService';
import { supabase } from '../../lib/supabase';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';

const UnifiedMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNative = Capacitor.isNativePlatform();
  // SAFE MODE: Auth System Fix - Profile menu only when authStatus === 'authenticated', never session alone
  const { user, userProfile, authStatus, AUTH_STATE, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [photographerData, setPhotographerData] = useState(null);
  const [loadingPhotographer, setLoadingPhotographer] = useState(false);

  // Load photographer data if user is a photographer
  useEffect(() => {
    if (userProfile?.role === 'photographer' && userProfile?.id) {
      setLoadingPhotographer(true);
      photographerService.getPhotographer(userProfile.id)
        .then((data) => {
          setPhotographerData(data);
        })
        .catch((error) => {
          console.error('Error loading photographer data:', error);
          setPhotographerData(null);
        })
        .finally(() => {
          setLoadingPhotographer(false);
        });
    } else {
      setPhotographerData(null);
    }
  }, [userProfile]);

  // Determine menu state - SAFE MODE: only show authenticated menu when authStatus === 'authenticated'
  const getMenuState = () => {
    if (authStatus !== AUTH_STATE?.AUTHENTICATED || !userProfile) {
      return 'guest';
    }

    // STATE 2: Client (Logged in)
    if (userProfile.role === 'customer') {
      return 'client';
    }

    // STATES 3-6: Photographer (various statuses)
    if (userProfile.role === 'photographer') {
      const status = photographerData?.status || 'pending';
      const isAvailable = photographerData?.available_for_booking || photographerData?.availableForBooking || false;

      // STATE 3: Pending Approval
      if (status === 'pending') {
        return 'photographer_pending';
      }

      // STATE 4: Suspended / Rejected
      if (status === 'suspended' || status === 'rejected') {
        return 'photographer_suspended';
      }

      // STATE 5: Approved & Available
      if (status === 'approved' && isAvailable) {
        return 'photographer_approved_available';
      }

      // STATE 6: Approved but NOT Available
      if (status === 'approved' && !isAvailable) {
        return 'photographer_approved_unavailable';
      }

      // Default fallback
      return 'photographer_pending';
    }

    return 'guest';
  };

  // Menu configuration based on state
  const getMenuItems = () => {
    const state = getMenuState();

    switch (state) {
      case 'guest':
        return [
          { path: '/', label: 'الرئيسية', icon: 'Home' },
          { path: '/', label: 'كيف يعمل', icon: 'Info', scrollTo: 'how-it-works' },
          { path: '/', label: 'الأسئلة الشائعة', icon: 'HelpCircle', scrollTo: 'faq' },
          { path: '/customer-login', label: 'تسجيل الدخول', icon: 'LogIn' },
          { path: '/customer-registration', label: 'إنشاء حساب', icon: 'UserPlus' },
          { path: '/', label: 'اتصل بنا', icon: 'Mail', scrollTo: 'contact' },
        ];

      case 'client':
        return [
          { path: '/', label: 'الرئيسية', icon: 'Home' },
          { path: '/customer-dashboard/bookings', label: 'حجوزاتي', icon: 'Calendar' },
          { path: '/booking-wizard', label: 'حجز جديد', icon: 'Plus' },
          { path: '/customer-dashboard/profile', label: 'الحساب', icon: 'User' },
          { path: '#logout', label: 'تسجيل الخروج', icon: 'LogOut', action: 'logout' },
        ];

      case 'photographer_pending':
        return [
          { path: '/photographer-dashboard', label: 'حالة الحساب', icon: 'Clock' },
          { path: '/photographer-dashboard/profile', label: 'تحديث الملف الشخصي', icon: 'User' },
          { path: '/customer-dashboard/help', label: 'الأسئلة الشائعة', icon: 'HelpCircle' },
          { path: '#logout', label: 'تسجيل الخروج', icon: 'LogOut', action: 'logout' },
        ];

      case 'photographer_suspended':
        return [
          { path: '/photographer-dashboard', label: 'حالة الحساب', icon: 'AlertCircle' },
          { path: '/customer-dashboard/help', label: 'اتصل بالدعم', icon: 'MessageCircle' },
          { path: '#logout', label: 'تسجيل الخروج', icon: 'LogOut', action: 'logout' },
        ];

      case 'photographer_approved_available':
        return [
          { path: '/photographer-dashboard/bookings?tab=pending', label: 'الطلبات الجديدة', icon: 'Bell' },
          { path: '/photographer-dashboard/bookings?tab=upcoming', label: 'الحجوزات القادمة', icon: 'Calendar' },
          { path: '/photographer-dashboard/bookings?tab=completed', label: 'الحجوزات المكتملة', icon: 'CheckCircle' },
          { path: '/photographer-dashboard/profile', label: 'الحساب', icon: 'User' },
          { path: '#logout', label: 'تسجيل الخروج', icon: 'LogOut', action: 'logout' },
        ];

      case 'photographer_approved_unavailable':
        return [
          { path: '/photographer-dashboard/bookings?tab=upcoming', label: 'الحجوزات القادمة', icon: 'Calendar' },
          { path: '/photographer-dashboard/profile', label: 'الحساب', icon: 'User' },
          { path: '/photographer-dashboard/availability', label: 'حالة التوفر', icon: 'Clock', disabled: true, info: 'غير متاح' },
          { path: '#logout', label: 'تسجيل الخروج', icon: 'LogOut', action: 'logout' },
        ];

      default:
        return [];
    }
  };

  const menuItems = getMenuItems();
  const menuState = getMenuState();

  const handleItemClick = (item) => {
    if (item.action === 'logout') {
      // SAFE MODE: Auth System Fix - Role-based logout redirect, never to "/"
      const role = userProfile?.role;
      signOut();
      if (role === 'photographer') navigate('/photographer-login');
      else if (role === 'admin') navigate('/admin');
      else navigate('/customer-login');
      setIsOpen(false);
    } else if (!item.disabled) {
      navigate(item.path);
      setIsOpen(false);
      
      // Handle scroll to section after navigation
      if (item.scrollTo) {
        setTimeout(() => {
          const element = document.getElementById(item.scrollTo) || 
                         document.querySelector(`[data-section="${item.scrollTo}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path.includes('?')) {
      const [basePath] = path.split('?');
      return location.pathname === basePath;
    }
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Don't show menu on auth pages
  const isAuthPage = location.pathname.includes('/login') || 
                     location.pathname.includes('/registration') || 
                     location.pathname.includes('/forgot-password') ||
                     location.pathname.includes('/reset-password');

  // Temporarily hide unified side menu inside customer/photographer dashboards only
  const isDashboardPage = location.pathname.startsWith('/customer-dashboard') ||
                          location.pathname.startsWith('/photographer-dashboard');

  if (isAuthPage || isDashboardPage) {
    return null;
  }

  return (
    <>
      {/* Overlay with Animation */}
      {isOpen && (
        <div
          className={cn(
            'fixed inset-0 bg-black/40 z-[55]',
            'transition-opacity duration-300',
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={() => setIsOpen(false)}
          style={{ touchAction: 'none' }}
        />
      )}

      {/* Sidebar Menu - safe area applied to aside so header (القائمة + X) stays below notch */}
      <aside
        className={cn(
          'fixed right-0 top-0 h-full bg-white z-[60]',
          'w-80 shadow-xl',
          'flex flex-col',
          'border-l border-slate-200',
          'transition-all duration-300 ease-out',
          isOpen 
            ? 'translate-x-0 opacity-100' 
            : 'translate-x-full opacity-0 pointer-events-none'
        )}
        style={{
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          boxSizing: 'border-box',
        }}
      >
        {/* Header with User Info */}
        <div className="bg-white border-b border-slate-200 flex-shrink-0 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              القائمة
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
              aria-label="إغلاق القائمة"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Icon name="X" size={20} className="text-slate-600" />
            </button>
          </div>
          
          {/* User Info */}
          {userProfile && (
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <div className="w-11 h-11 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                {userProfile?.avatar_url ? (
                  <img
                    src={userProfile.avatar_url}
                    alt={userProfile.full_name || 'مستخدم'}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={22} className="text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0 text-right">
                <p className="font-semibold text-slate-900 text-sm truncate" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                  {userProfile.full_name || 'مستخدم'}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {userProfile.role === 'customer' ? 'عميل' : userProfile.role === 'photographer' ? 'مصور' : 'مستخدم'}
                </p>
              </div>
            </div>
          )}
          
          {/* Current Page Indicator - Hidden */}
          {/* {menuItems.some(item => isActive(item.path)) && (
            <div className="mt-4 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                الصفحة الحالية:
              </p>
              <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                {menuItems.find(item => isActive(item.path))?.label || '—'}
              </p>
            </div>
          )} */}
        </div>

        {/* Menu Items */}
        <nav 
          className="flex-1 overflow-y-auto overflow-x-hidden p-4"
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          }}
        >
          <div className="space-y-2">
            {menuItems.map((item, index) => (
              <button
                key={`${item.path}-${item.label}-${index}`}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={cn(
                  'flex items-center gap-3 px-4 py-3 rounded-xl transition-all w-full text-right',
                  'touch-manipulation min-h-[56px] relative',
                  item.disabled
                    ? 'bg-slate-50 text-slate-400 cursor-not-allowed'
                    : isActive(item.path)
                    ? 'bg-slate-100 border-r-3 border-blue-600'
                    : 'bg-transparent hover:bg-slate-50 active:bg-slate-100 text-slate-800'
                )}
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                }}
              >
                {/* Active Indicator Bar */}
                {isActive(item.path) && !item.disabled && (
                  <div className="absolute right-0 top-0 bottom-0 w-1 rounded-l-full" style={{ backgroundColor: 'var(--color-menu-icon)' }}></div>
                )}
                
                {/* Icon Container */}
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all',
                  item.disabled
                    ? 'bg-slate-200'
                    : isActive(item.path)
                    ? 'bg-slate-100'
                    : 'bg-slate-100'
                )}>
                  <Icon 
                    name={item.icon} 
                    size={20} 
                    color={item.disabled ? '#94a3b8' : 'var(--color-menu-icon)'} 
                  />
                </div>
                
                {/* Label Container */}
                <div className="flex-1 text-right min-w-0">
                  <span className={cn(
                    'font-medium text-sm block',
                    item.disabled
                      ? 'text-slate-400'
                      : isActive(item.path)
                      ? 'text-slate-900 font-semibold'
                      : 'text-slate-700'
                  )} style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                    {item.label}
                  </span>
                  {item.info && (
                    <span className={cn(
                      'text-xs mt-0.5 block',
                      item.disabled ? 'text-slate-400' : 'text-slate-500'
                    )}>
                      {item.info}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </nav>
      </aside>

      {/* Toggle Button - Top Left with Animation */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed left-4 z-[55]',
          !isNative && 'top-4 md:top-6',
          'w-11 h-11 rounded-xl shadow-lg transition-all duration-300',
          'bg-white border border-slate-200',
          'flex items-center justify-center',
          'touch-manipulation',
          'hover:bg-slate-50 active:scale-95 hover:shadow-xl',
          'md:left-6 md:w-12 md:h-12',
          isOpen && 'bg-slate-100'
        )}
        aria-label={isOpen ? "إغلاق القائمة" : "فتح القائمة"}
        style={{
          WebkitTapHighlightColor: 'transparent',
          ...(isNative && {
            top: 'calc(1rem + env(safe-area-inset-top, 0px))',
          }),
        }}
      >
        <Icon 
          name={isOpen ? "X" : "Menu"} 
          size={20} 
          color="var(--color-menu-icon)"
          className="md:w-5 md:h-5 transition-transform duration-300" 
        />
      </button>
    </>
  );
};

export default UnifiedMenu;
